import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
} from "recharts"

import type { AiTokenFeature } from "@productlane/lib"
import { AI_TOKEN_FEATURE_LABELS } from "@productlane/lib"
import { cn } from "@productlane/lib/src/browser"

import { api } from "@/lib/api"

interface AiTokenUsageChartProps {
	className?: string
	startDate?: Date
	endDate?: Date
}

const FALLBACK_COLOR = "#64748b"

const FEATURE_COLORS: Record<string, string> = {
	copilot_draft: "#4CB782",
	copilot_chat: "#0ea5e9",
	docs_generation: "#6366f1",
	docs_suggestion: "#8b5cf6",
	docs_image: "#a855f7",
	site_import: "#d946ef",
	changelog: "#f59e0b",
	translation: "#10b981",
	auto_reply: "#ec4899",
	thread_title: "#f97316",
	issue_summary: "#14b8a6",
	agent_chat: "#94a3b8",
	routines: "#475569",
	close_loop: "#0891b2",
	bug_inspect: "#dc2626",
	agent_recommendations: "#7c3aed",
	request_triage: "#65a30d",
	link_suggestions: "#2563eb",
	slack_assistant: "#4a154b",
	other: FALLBACK_COLOR,
}

export const featureLabel = (feature: string) =>
	AI_TOKEN_FEATURE_LABELS[feature as AiTokenFeature] ?? feature

export const featureColor = (feature: string) =>
	FEATURE_COLORS[feature] ?? FALLBACK_COLOR

const usd = (value: number) => `$${value.toFixed(2)}`

interface TooltipPayload {
	active?: boolean
	label?: string
	payload?: Array<{ name: string; value: number; color: string }>
}

const UsageTooltip = ({ active, payload, label }: TooltipPayload) => {
	if (!active || !payload?.length) return null
	const rows = payload.filter((p) => p.value > 0)
	const total = rows.reduce((sum, p) => sum + p.value, 0)
	return (
		<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
			<p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
				{label}
			</p>
			{rows.length === 0 ? (
				<p className="text-sm text-gray-500 dark:text-gray-400">No usage</p>
			) : (
				rows.map((row) => (
					<p
						key={row.name}
						className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
					>
						<span
							className="inline-block size-2 rounded-full"
							style={{ backgroundColor: row.color }}
						/>
						{featureLabel(row.name)}: {usd(row.value)}
					</p>
				))
			)}
			{rows.length > 1 && (
				<p className="mt-2 border-t border-gray-200 pt-2 text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-white">
					Total: {usd(total)}
				</p>
			)}
		</div>
	)
}

export const AiTokenUsageChart = ({
	className,
	startDate,
	endDate,
}: AiTokenUsageChartProps) => {
	const query = api.workspace.getAiTokenUsage.useQuery(
		{ startDate, endDate },
		{ cacheTime: Infinity, staleTime: Infinity },
	)

	// Skeleton only while the first fetch is in flight. On error or empty the
	// chart frame still renders (empty axes) instead of hanging on the loader.
	if (query.isLoading) {
		return (
			<div className={cn("animate-pulse pb-6 pt-10", className)}>
				<div className="mb-6 h-6 w-48 rounded bg-gray-200 dark:bg-gray-700" />
				<div className="h-52 w-full rounded bg-gray-100 dark:bg-gray-800" />
			</div>
		)
	}

	const chartData = query.data?.chartData ?? []
	const features = query.data?.features ?? []
	const totalsByFeature = query.data?.totalsByFeature ?? {}
	// Only chart features that actually have spend, so the breakdown stays honest.
	const activeFeatures = features.filter(
		(feature) => (totalsByFeature[feature] ?? 0) > 0,
	)
	const rangeTotal = activeFeatures.reduce(
		(sum, feature) => sum + (totalsByFeature[feature] ?? 0),
		0,
	)
	const fmtDate = (d?: Date) => (d ? `${d.getDate()}.${d.getMonth() + 1}` : "")
	const rangeLabel = `${fmtDate(startDate)} - ${fmtDate(endDate)}`

	return (
		<div className={cn("w-full pb-2 pt-4", className)}>
			<div className="relative h-64 w-full [&_*]:outline-none">
				{activeFeatures.length === 0 && (
					<div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
						No AI usage yet for this period
					</div>
				)}
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={chartData}
						margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
						barCategoryGap="20%"
						maxBarSize={10}
					>
						<CartesianGrid
							className="stroke-gray-200 dark:stroke-gray-700"
							vertical={false}
						/>
						<XAxis
							dataKey="date"
							className="text-xs text-gray-500 dark:text-gray-400"
							tickLine={false}
							interval="preserveStartEnd"
							minTickGap={28}
							axisLine={{ className: "stroke-gray-300 dark:stroke-gray-700" }}
						/>
						<Tooltip
							content={<UsageTooltip />}
							cursor={{ className: "fill-gray-100 dark:fill-gray-800" }}
						/>
						{activeFeatures.map((feature) => (
							<Bar
								key={feature}
								dataKey={feature}
								stackId="usage"
								fill={featureColor(feature)}
								radius={feature === activeFeatures.at(-1) ? [4, 4, 0, 0] : 0}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			</div>

			{activeFeatures.length > 0 && (
				<div className="mt-4">
					<div className="-mx-4 flex items-center justify-between border-b border-gray-200 px-4 py-2 text-xs text-tertiary dark:border-gray-700/80">
						<span>Feature</span>
						<span>Usage ({rangeLabel})</span>
					</div>
					{activeFeatures.map((feature) => (
						<div
							key={feature}
							className="-mx-4 flex items-center justify-between border-b border-gray-200 px-4 py-2.5 text-sm dark:border-gray-700/80"
						>
							<span className="flex items-center gap-2.5">
								<span
									className="size-2 rounded-full"
									style={{ backgroundColor: featureColor(feature) }}
								/>
								<span className="text-primary">{featureLabel(feature)}</span>
							</span>
							<span className="tabular-nums text-primary">
								{usd(totalsByFeature[feature] ?? 0)}
							</span>
						</div>
					))}
					<div className="-mx-4 flex items-center justify-between border-gray-200 px-4 py-2.5 text-sm font-medium dark:border-gray-700/80">
						<span className="text-primary">Total spent</span>
						<span className="tabular-nums text-primary">{usd(rangeTotal)}</span>
					</div>
				</div>
			)}
		</div>
	)
}
